import { Injectable, OnDestroy } from '@angular/core';
import { LoggingService, LogSeverity } from '@core/services/logging.service';
import { onCLS, onINP, onLCP, onFCP, onTTFB } from 'web-vitals';

/**
 * Human-readable Web Vitals names and units.
 */
const VITAL_LABELS: Record<string, { label: string; unit?: 'ms' | '' }> = {
  TTFB: { label: 'Time to First Byte (ms)', unit: 'ms' },
  FCP: { label: 'First Contentful Paint (ms)', unit: 'ms' },
  LCP: { label: 'Largest Contentful Paint (ms)', unit: 'ms' },
  CLS: { label: 'Cumulative Layout Shift', unit: '' },
  INP: { label: 'Interaction to Next Paint (ms)', unit: 'ms' },
};

type VitalValue = { value: number; rating?: string; details?: any };

/**
 * Periodically captures browser/environment metadata and performance signals.
 * - Emits one **combined Web Vitals snapshot** after an initial delay so values have time to populate.
 * - Tracks Long Tasks to approximate Total Blocking Time (TBT).
 * - Logs resource timing summaries hourly (configurable via code changes).
 * - Adds error hooks for `window.error` and `unhandledrejection`.
 */
@Injectable({ providedIn: 'root' })
export class BrowserTelemetryService implements OnDestroy {
  private intervalId: number | undefined;
  private tbtMs = 0;
  private vitalsFlushed = false;
  private readonly VITALS_FIRST_FLUSH_DELAY_MS = 8000;
  private readonly vitals: Record<string, VitalValue> = {};

  constructor(private logger: LoggingService) {
    this.captureEnvironmentMetadata();
    this.initWebVitals();
    this.initLongTasks();
    this.initErrorHooks();
    this.logNavTimingOnce();

    this.intervalId = window.setInterval(() => {
      this.logResourceSummary();
      this.captureEnvironmentMetadata();
      this.logger.writeLog('[BrowserTelemetry] Rolling total blocking time', {
        severity: LogSeverity.INFO,
        detail: { totalBlockingTimeMs: this.tbtMs },
      });
      this.tbtMs = 0;
    }, 60 * 60_000);
  }

  private readonly VITALS_MAX_WAIT_MS = 12_000; // max cap so we don't hang forever

  private vitalsSeen = {
    FCP: false,
    LCP: false,
    CLS: false,
    INP: false,
    TTFB: false,
  };

  private async initWebVitals(): Promise<void> {
    try {
      const upsert = (m: any) => {
        const rounded = m.name === 'CLS' ? m.value : Math.round(m.value);
        this.vitals[m.name] = { value: rounded, rating: m.rating, details: m.attribution ?? undefined };
        if (m.name in this.vitalsSeen) this.vitalsSeen[m.name as keyof typeof this.vitalsSeen] = true;
        // If we haven't flushed yet and we see a key milestone (FCP or LCP), we can flush soon.
        if (!this.vitalsFlushed && (this.vitalsSeen.FCP || this.vitalsSeen.LCP)) {
          // Debounce a tiny bit to allow late-arriving updates
          window.clearTimeout(this._pendingVitalsFlush);
          this._pendingVitalsFlush = window.setTimeout(() => this.flushVitalsOnce(), 1000);
        }
      };

      onCLS(upsert, { reportAllChanges: true });
      onINP(upsert, { reportAllChanges: true });
      onLCP(upsert, { reportAllChanges: true });
      onFCP(upsert);
      onTTFB(upsert);

      // Safety: flush after max wait even if FCP/LCP never show
      this._pendingVitalsFlush = window.setTimeout(() => this.flushVitalsOnce(), this.VITALS_MAX_WAIT_MS);
    } catch {
      // web-vitals not installed; skip
    }
  }

  private _pendingVitalsFlush: number | undefined;

  private flushVitalsOnce(): void {
    if (this.vitalsFlushed) return;
    this.vitalsFlushed = true;
    if (this._pendingVitalsFlush) {
      window.clearTimeout(this._pendingVitalsFlush);
      this._pendingVitalsFlush = undefined;
    }

    // Build human-readable payload; include "not observed" when missing.
    const pretty: Record<string, unknown> = {};
    const add = (name: 'TTFB' | 'FCP' | 'LCP' | 'CLS' | 'INP') => {
      const meta = VITAL_LABELS[name];
      const v = this.vitals[name];
      pretty[meta.label] = v
        ? { value: v.value, rating: v.rating, attribution: v.details || undefined }
        : { value: null, note: 'Not observed before flush' };
    };
    add('TTFB');
    add('FCP');
    add('LCP');
    add('CLS');
    add('INP');

    this.logger.writeLog('[BrowserTelemetry] Web Vitals snapshot', {
      severity: LogSeverity.INFO,
      detail: { capturedAt: new Date().toISOString(), ...pretty },
    });
  }

  private initLongTasks(): void {
    if (!('PerformanceObserver' in window)) return;
    try {
      const po = new PerformanceObserver((list) => {
        for (const e of list.getEntries() as PerformanceEntryList & any) {
          const over50 = Math.max(0, e.duration - 50);
          this.tbtMs += over50;
        }
      });
      po.observe({ type: 'longtask', buffered: true as any });
    } catch {
      /* ignore */
    }
  }

  private logNavTimingOnce(): void {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    const paints = performance.getEntriesByType('paint') as PerformanceEntry[];
    const fcp = paints.find((p) => p.name === 'first-contentful-paint')?.startTime;

    if (!nav) return;

    this.logger.writeLog('[BrowserTelemetry] Page load timings (ms)', {
      severity: LogSeverity.INFO,
      detail: {
        'Time to First Byte (ms)': nav.responseStart,
        'DOM Interactive (ms)': nav.domInteractive,
        'DOM Content Loaded (ms)': nav.domContentLoadedEventEnd,
        'Load Event End (ms)': nav.loadEventEnd,
        'First Contentful Paint (ms)': fcp ?? null,
        transferSize: (nav as any).transferSize ?? null,
        encodedBodySize: (nav as any).encodedBodySize ?? null,
      },
    });
  }

  private logResourceSummary(final = false): void {
    if (!('getEntriesByType' in performance)) return;
    const res = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
    if (!res.length) return;

    const byType: Record<string, number> = {};
    let totalTransfer = 0;
    let slow = 0;
    let largest: { name: string; size: number } | null = null;

    for (const r of res) {
      const t = r.initiatorType || 'other';
      byType[t] = (byType[t] || 0) + 1;
      const size = (r as any).transferSize ?? 0;
      totalTransfer += size;
      if (r.duration > 2000) slow++;
      if (!largest || size > largest.size) largest = { name: r.name, size };
    }

    this.logger.writeLog('[BrowserTelemetry] Resource summary', {
      severity: LogSeverity.INFO,
      detail: {
        totalResources: res.length,
        resourcesByType: byType,
        totalTransferBytes: totalTransfer,
        slowResourcesOver2s: slow,
        largestResource: largest,
        final,
      },
    });
  }

  private initErrorHooks(): void {
    window.addEventListener('error', (e) => {
      this.logger.writeLog('[BrowserTelemetry] Error: window.error', {
        severity: LogSeverity.WARNING,
        detail: {
          message: e.message,
          filename: e.filename,
          lineno: e.lineno,
          colno: e.colno,
        },
      });
    });
    window.addEventListener('unhandledrejection', (e) => {
      this.logger.writeLog('[BrowserTelemetry] Error: unhandledrejection', {
        severity: LogSeverity.ERROR,
        detail: {
          reason: (e as any).reason?.message || String((e as any).reason || 'unknown'),
        },
      });
    });
  }

  private captureEnvironmentMetadata(): void {
    const metadata = {
      capturedAt: new Date().toISOString(),
      screen: {
        width: window.screen?.width ?? null,
        height: window.screen?.height ?? null,
        availWidth: window.screen?.availWidth ?? null,
        availHeight: window.screen?.availHeight ?? null,
        orientation: (window.screen?.orientation || {}).type || 'unknown',
        colorDepth: window.screen?.colorDepth ?? null,
        pixelDepth: window.screen?.pixelDepth ?? null,
      },
      viewport: {
        innerWidth: window.innerWidth ?? null,
        innerHeight: window.innerHeight ?? null,
        outerWidth: window.outerWidth ?? null,
        outerHeight: window.outerHeight ?? null,
        scrollX: window.scrollX ?? null,
        scrollY: window.scrollY ?? null,
        devicePixelRatio: window.devicePixelRatio ?? null,
      },
      network: {
        userAgent: navigator.userAgent ?? 'unknown',
        platform: navigator.platform ?? 'unknown',
        language: navigator.language ?? 'unknown',
        languages: navigator.languages ?? [],
        online: navigator.onLine ?? null,
        connectionType: (navigator as any).connection?.effectiveType || 'unknown',
        rtt: (navigator as any).connection?.rtt ?? null,
        saveData: (navigator as any).connection?.saveData ?? null,
        maxTouchPoints: navigator.maxTouchPoints ?? null,
        hardwareConcurrency: navigator.hardwareConcurrency ?? null,
      },
      page: {
        visibility: document.visibilityState ?? 'unknown',
        referrer: document.referrer ?? '',
        title: document.title ?? '',
        location: {
          hostname: location.hostname ?? '',
          pathname: location.pathname ?? '',
          hash: location.hash.split('?')[0] ?? '',
          protocol: location.protocol ?? '',
          origin: location.origin ?? '',
        },
      },
      security: {
        isSecureContext: typeof window.isSecureContext === 'boolean' ? window.isSecureContext : null,
        crossOriginIsolated: typeof window.crossOriginIsolated === 'boolean' ? window.crossOriginIsolated : null,
      },
    };

    this.logger.writeLog('[BrowserTelemetry] Browser snapshot', {
      severity: LogSeverity.INFO,
      detail: metadata,
    });
  }

  ngOnDestroy(): void {
    if (this.intervalId) clearInterval(this.intervalId);
  }
}
